/**
 * components/toastConfig.tsx
 * Premium renderers for react-native-toast-message — visually IDENTICAL to
 * SK-Hire (same card shape, palette, fonts, accent rail, icon tile, shadow).
 *
 * The palette is pinned to SK-Hire's exact token values (light + dark) from
 * SK_Hire/src/theme/colors.ts, so the toast looks the same as SK-Hire
 * regardless of SKCatalog's own theme colors. Fonts match SK-Hire's
 * typography.ts (IBM Plex Sans · md title / sm subtitle · 600 weight).
 */
import React from 'react';
import { View, Text, Platform } from 'react-native';
import type { ToastConfig, ToastConfigParams } from 'react-native-toast-message';
import { useTheme } from '../context/themeContext';
import Icon from './icon';

const isWeb = Platform.OS === 'web';

// SK-Hire toast palette — exact hex from SK_Hire/src/theme/colors.ts. Only the
// tokens the toast actually renders are pinned here.
const HIRE = {
  light: {
    card: '#FFFFFF', ink: '#2B2F36', ink3: '#7C828C', line: '#E7E4DC',
    success: '#1F9D5B', successSoft: '#E6F5EC', successLine: '#A6DDBE',
    danger: '#CB4B4B', dangerSoft: '#FBEAEA', dangerLine: '#ECAEAE',
    info: '#2F6FE0', infoSoft: '#E9F0FD', infoLine: '#B7CDF6',
  },
  dark: {
    card: '#212329', ink: '#F4F4F2', ink3: '#9A9C9E', line: '#33343B',
    success: '#54C796', successSoft: '#19271F', successLine: '#315340',
    danger: '#E97676', dangerSoft: '#2C1E1E', dangerLine: '#6A3838',
    info: '#6E92F0', infoSoft: '#222B3D', infoLine: '#3A4A6B',
  },
};

// SK-Hire fonts (theme/typography.ts): md = 13 (title), sm = 12 (subtitle), 600.
const FS = { md: 13, sm: 12 };
const SEMIBOLD = '600' as const;

type Tone = 'success' | 'error' | 'info';

function ToastItem({ tone, text1, text2 }: { tone: Tone; text1?: string; text2?: string }) {
  const { isDark } = useTheme();
  const c = isDark ? HIRE.dark : HIRE.light;

  const t =
    tone === 'success'
      ? { fg: c.success, soft: c.successSoft, line: c.successLine, icon: 'check-circle' }
      : tone === 'info'
        ? { fg: c.info, soft: c.infoSoft, line: c.infoLine, icon: 'info' }
        : { fg: c.danger, soft: c.dangerSoft, line: c.dangerLine, icon: 'alert-circle' };

  return (
    <View
      style={{
        flexDirection: 'row', alignItems: 'flex-start', gap: 12, width: '92%', maxWidth: 440,
        paddingVertical: 13, paddingHorizontal: 14, borderRadius: 14, overflow: 'hidden',
        backgroundColor: c.card, borderWidth: 1, borderColor: c.line,
        ...(isWeb
          ? ({ boxShadow: '0 18px 44px -12px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.25)', zIndex: 999999 } as any)
          : { elevation: 14, shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 16, shadowOffset: { width: 0, height: 8 } }),
      }}
    >
      {/* accent rail */}
      <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: t.fg }} />
      <View style={{ width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: t.soft, borderWidth: 1, borderColor: t.line, marginLeft: 2 }}>
        <Icon name={t.icon} size={16} color={t.fg} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        {!!text1 && <Text style={{ fontSize: FS.md, fontWeight: SEMIBOLD, color: c.ink, letterSpacing: -0.1 }}>{text1}</Text>}
        {!!text2 && <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 2, lineHeight: FS.sm * 1.45 }}>{text2}</Text>}
      </View>
    </View>
  );
}

export const toastConfig: ToastConfig = {
  success: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="success" text1={text1} text2={text2} />,
  error: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="error" text1={text1} text2={text2} />,
  // SK-Hire ships only success/error; SKCatalog also fires toast.info (e.g. items,
  // mediaGallery), so info is rendered here in SK-Hire's exact info palette.
  info: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="info" text1={text1} text2={text2} />,
};
